import React, { useEffect, useRef, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import Page from '@afosto/app-components-react/Page';
import translations from './translations';
import { proxy as proxyApi } from '@afosto/quicq/api/Proxy';
import Grid from '@material-ui/core/Grid';
import ProxyFormDrawer from '../../components/ProxyFormDrawer';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import DataGrid from '@afosto/app-components-react/DataGrid';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import routesFormatter from './formatters';

const fetchProxies = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => proxyApi.listQuicqProxies(requestOptions),
    { transformData: data => data.reduce((acc, item) => [...acc, { ...item, id: item?.key }], []) },
  );

const Proxies = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['qcq:proxies:read']);

  const intl = useIntl();
  const dataRef = useRef([]);
  const [proxy, setProxy] = useState(null);
  const [showCreateProxy, setShowCreateProxy] = useState(false);

  const dataSource = useDataGrid(fetchProxies, {
    modules: ['dataProperties', 'activeItem', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'key',
          name: intl.formatMessage({
            id: 'dataGrid.columns.proxies.key',
            defaultMessage: 'Key',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'routes',
          name: intl.formatMessage({
            id: 'dataGrid.columns.proxies.origin',
            defaultMessage: 'Origin',
            description: 'The column name for column scopes',
          }),
          type: 'routes',
        },
      ],
    },
  });

  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  useEffect(() => {
    dataRef.current = dataSource.data;
  });

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      if (activeItem) {
        setProxy(findObjectInArray(dataRef.current, 'key', activeItem));
      } else {
        setProxy(null);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Page
      title={intl.formatMessage(translations.title)}
      fullWidth
      primaryAction={{
        text: intl.formatMessage(translations.primaryAction.label),
        action: () => setShowCreateProxy(true),
      }}
    >
      <Grid container spacing={2}>
        <DataGrid dataSource={dataSource} formatters={{ routes: routesFormatter }} />
      </Grid>
      <ProxyFormDrawer
        open={showCreateProxy || activeItem}
        onClose={() => {
          setShowCreateProxy(false);
          setActiveItem(null);
        }}
        proxy={proxy}
        onSuccess={() => {
          dataSource.reloadDataSource();
          setActiveItem(null);
          setShowCreateProxy(false);
        }}
      />
    </Page>
  );
};

export default Proxies;
